import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addAdToBatch,
  clearBatch,
  useAdBatchSlice,
  AdBatchState,
} from "@redux/slices/adBatchSlice";
import { useUserAnalytics } from "./useAnalytics";
import { EventType } from "@utils/analytics";
import { store } from "@redux/store";

let hasCheckedStaleAds = false;

let globalTimerRef: ReturnType<typeof setInterval> | null = null;
//@ts-ignore
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let timerStartedAt: number | null = null;

export const useAdBatchTracking = () => {
  const timerInterval =
    useSelector(
      (state: any) => state.globalData?.app_settings?.ad_batch_interval
    ) || 5 * 60 * 1000;
  const dispatch = useDispatch();
  const analytics = useUserAnalytics();
  const { getAdCount, getTotalViewCount, hasStaleAds } = useAdBatchSlice();

  const isLoggedIn = useSelector((state: any) => !!state.userInfo?.user?.id);
  const hasInitialized = useRef(false);

  const sendBatch = useCallback(async () => {
    const currentState = store.getState();
    const adBatchState = currentState?.adBatch as AdBatchState;
    const currentAdBatch = adBatchState?.adBatch ?? {};

    const adKeys = Object.keys(currentAdBatch);
    if (adKeys.length === 0) {
      return;
    }

    const adsToSend = Object.values(currentAdBatch);

    try {
      for (const ad of adsToSend) {
        if (analytics?._track) {
          await analytics._track(EventType.VIEW_BANNER, {
            ...ad.properties,
            view_count: ad.viewCount,
            first_seen_at: ad.firstSeenAt,
            last_seen_at: ad.lastSeenAt,
            is_batched: true,
            batch_timestamp: new Date().toISOString(),
          });
        } else {
          console.warn(
            "[AdBatch] Analytics not available, batch will be retried"
          );
          return; // Don't clear batch if we couldn't send
        }
      }

      dispatch(clearBatch());
    } catch (error) {
      console.error("[AdBatch] Failed to send batch:", error);
    }
  }, [dispatch, analytics]);

  const stopTimer = useCallback(() => {
    if (globalTimerRef) {
      clearInterval(globalTimerRef);
      globalTimerRef = null;
      timerStartedAt = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    if (globalTimerRef) {
      return;
    }

    const currentState = store.getState();
    const adBatchState = currentState?.adBatch as AdBatchState;
    const hasData = Object.keys(adBatchState?.adBatch ?? {}).length > 0;

    if (!hasData) {
      return;
    }

    globalTimerRef = setInterval(() => {
      const state = store.getState();
      const batchState = state?.adBatch as AdBatchState;
      const batchHasData = Object.keys(batchState?.adBatch ?? {}).length > 0;

      if (!batchHasData) {
        stopTimer();
        return;
      }

      sendBatch();
    }, timerInterval);

    timerStartedAt = Date.now();
  }, [sendBatch, stopTimer]);

  useEffect(() => {
    if (!isLoggedIn || hasCheckedStaleAds || hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;
    hasCheckedStaleAds = true;

    if (hasStaleAds(timerInterval)) {
      sendBatch();
    }
  }, [isLoggedIn, hasStaleAds, sendBatch]);

  const trackBatchedAdEvent = useCallback(
    (eventName: string, properties: Record<string, any>) => {
      dispatch(addAdToBatch(properties));

      if (!globalTimerRef && isLoggedIn) {
        startTimer();
      }
    },
    [dispatch, getAdCount, isLoggedIn, startTimer]
  );

  return {
    trackBatchedAdEvent,
    getAdCount,
    getTotalViewCount,
    sendBatch,
  };
};

export default useAdBatchTracking;
